import { Navigate, Route, Routes, useLocation, useParams } from 'react-router';
import { tr } from './i18n';
import { useSession } from './session';
import { AppShell, AdminShell } from './layout';
import { Loading } from './ui';
import { Login, Signup, GoogleSignup, VerifyEmail, ForgotPassword, ResetPassword, AcceptInvite, MfaVerify } from './pages/auth';
import { Dashboard } from './pages/Dashboard';
import { Devices, DeviceDetail, AddComputer } from './pages/devices';
import { ArchivePage } from './pages/archives';
import { ConnectPhone } from './pages/phone';
import { PlanPage, Plans, PlanWizard } from './pages/plans';
import { RestoreWizard, Restores, RestoreDetail } from './pages/restores';
import { Users, Audit, Alerts, Billing, Settings } from './pages/tenant';
import { Account } from './pages/account';
import { Terms } from './pages/terms';
import { Accessibility } from './pages/accessibility';
import { SharedLinkPage, SharedLinks } from './pages/shares';
import { AdminOverview, AdminTenants, AdminTenant, AdminPlans, AdminUsers, AdminQueues, AdminStorage, AdminReleases, AdminMaintenance, AdminAudit } from './pages/admin';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { me, loading } = useSession();
  const loc = useLocation();
  if (loading) return <div className="content"><Loading /></div>;
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (me.mfaPending) return <Navigate to="/mfa" replace />;
  return <>{children}</>;
}

/** Old addresses (bookmarks, emails) keep working after pages were renamed. */
function Moved({ to }: { to: (p: Record<string, string | undefined>) => string }) {
  const params = useParams();
  const { search } = useLocation();
  return <Navigate to={to(params) + (to(params).includes('?') ? '' : search)} replace />;
}

function Home() {
  const { me } = useSession();
  if (!me) return <Navigate to="/login" replace />;
  if (me.memberships.length) return <Navigate to={`/t/${me.memberships[0]!.tenantId}`} replace />;
  if (me.isSuperAdmin) return <Navigate to="/admin" replace />;
  return <Navigate to="/account" replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/signup/google" element={<GoogleSignup />} />
      <Route path="/mfa" element={<MfaVerify />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/invite" element={<AcceptInvite />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/accessibility" element={<Accessibility />} />
      <Route path="/" element={<RequireAuth><Home /></RequireAuth>} />
      <Route path="/account" element={<RequireAuth><AppShell><Account /></AppShell></RequireAuth>} />
      <Route path="/t/:tenantId" element={<RequireAuth><AppShell /></RequireAuth>}>
        <Route index element={<Dashboard />} />
        <Route path="devices" element={<Devices />} />
        <Route path="devices/add" element={<AddComputer />} />
        <Route path="devices/add-phone" element={<ConnectPhone />} />
        <Route path="devices/:deviceId" element={<DeviceDetail />} />
        <Route path="devices/:deviceId/archives/:archiveId" element={<ArchivePage />} />
        <Route path="plans" element={<Plans />} />
        <Route path="plans/new" element={<PlanWizard />} />
        <Route path="plans/:planId" element={<PlanPage />} />
        <Route path="policies" element={<Moved to={(p) => `/t/${p.tenantId}/plans`} />} />
        <Route path="policies/:policyId" element={<Moved to={(p) => `/t/${p.tenantId}/plans/${p.policyId}`} />} />
        <Route path="restore" element={<RestoreWizard />} />
        <Route path="restores" element={<Restores />} />
        <Route path="restores/:restoreId" element={<RestoreDetail />} />
        <Route path="users" element={<Users />} />
        <Route path="audit" element={<Audit />} />
        <Route path="alerts" element={<Alerts />} />
        <Route path="notifications" element={<Moved to={(p) => `/t/${p.tenantId}/alerts?tab=messages`} />} />
        <Route path="webhooks" element={<Moved to={(p) => `/t/${p.tenantId}/settings`} />} />
        <Route path="billing" element={<Billing />} />
        <Route path="settings" element={<Settings />} />
        <Route path="shares" element={<SharedLinks />} />
        <Route path="shares/:shareId" element={<SharedLinkPage />} />
      </Route>
      <Route path="/admin" element={<RequireAuth><AdminShell /></RequireAuth>}>
        <Route index element={<AdminOverview />} />
        <Route path="tenants" element={<AdminTenants />} />
        <Route path="tenants/:tenantId" element={<AdminTenant />} />
        <Route path="plans" element={<AdminPlans />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="queues" element={<AdminQueues />} />
        <Route path="storage" element={<AdminStorage />} />
        <Route path="releases" element={<AdminReleases />} />
        <Route path="maintenance" element={<AdminMaintenance />} />
        <Route path="audit" element={<AdminAudit />} />
      </Route>
      <Route path="*" element={<div className="content"><h1>{tr('Page not found')}</h1></div>} />
    </Routes>
  );
}
